import { define, html, prop } from '@vielzeug/ore';
import { computed, signal, watch } from '@vielzeug/ripple';

import type { ComponentSize, RoundedSize, ThemeColor } from '../../types';

import '../icon/icon';
import { roundableBundle, sizableBundle, themableBundle } from '../../shared';
import { colorThemeMixin, roundedVariantMixin, sizeVariantMixin } from '../../styles';
import componentStyles from './avatar.css?inline';

export type AvatarStatus = 'online' | 'offline' | 'busy' | 'away';

const STATUS_LABELS: Record<AvatarStatus, string> = {
  away: 'Away',
  busy: 'Busy',
  offline: 'Offline',
  online: 'Online',
};

/** Avatar component properties */
export type OreAvatarProps = {
  /** Alt text for the image; also used to derive initials when no `initials` prop is given */
  alt?: string;
  /** Theme color (used for initials background) */
  color?: ThemeColor;
  /** Explicit initials to display when no image is available (e.g. "JD") */
  initials?: string;
  /** Border radius */
  rounded?: RoundedSize | '';
  /** Component size */
  size?: ComponentSize;
  /** Image source URL */
  src?: string;
  /** Online presence indicator */
  status?: AvatarStatus;
};

/**
 * Displays a user avatar: image → initials → generic fallback icon, in that priority order.
 *
 * @element ore-avatar
 *
 * @attr {string} src - Image source URL
 * @attr {string} alt - Image alt text (also used to derive initials)
 * @attr {string} initials - Explicit initials (up to 2 chars)
 * @attr {string} color - Theme color for initials background: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error'
 * @attr {string} size - 'sm' | 'md' | 'lg'
 * @attr {string} rounded - Border radius: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full'
 * @attr {string} status - 'online' | 'offline' | 'busy' | 'away'
 *
 * @slot - Fallback content shown when image and initials are unavailable.
 * @cssprop --avatar-size - Diameter of the avatar
 * @cssprop --avatar-bg - Background color
 * @cssprop --avatar-color - Text/icon color
 * @cssprop --avatar-radius - Border radius
 * @cssprop --avatar-border - Border shorthand
 * @cssprop --avatar-border-color - Border color (also controls status dot border)
 *
 * @part avatar - Avatar root container.
 * @part img - Image element.
 * @part initials - Initials fallback element.
 * @part fallback - Shadow part for the `fallback` element.
 * @part status - Status indicator element.
 * @part overflow - Overflow indicator element.
 * @example
 * ```html
 * <ore-avatar src="/jane.jpg" alt="Jane Doe"></ore-avatar>
 * <ore-avatar initials="JD" color="primary"></ore-avatar>
 * <ore-avatar alt="John Smith" status="online"></ore-avatar>
 * ```
 */
export const AVATAR_TAG = 'ore-avatar' as const;
define<OreAvatarProps>(AVATAR_TAG, {
  props: {
    ...themableBundle,
    ...sizableBundle,
    ...roundableBundle,
    alt: prop.string(),
    initials: prop.string(),
    src: prop.string(),
    status: prop.string<AvatarStatus>(),
  },
  setup(props) {
    const imgFailed = signal(false);

    // Reset stale error state whenever src changes
    watch(props.src, () => {
      imgFailed.value = false;
    });

    // Attach load/error listeners reactively when the img element mounts
    const attachImgListeners = (el: HTMLImageElement | null) => {
      if (!el) return;

      el.addEventListener('error', () => {
        imgFailed.value = true;
      });
      el.addEventListener('load', () => {
        imgFailed.value = false;
      });
    };
    const derivedInitials = computed(() => {
      if (props.initials.value) return props.initials.value.slice(0, 2);

      const alt = props.alt.value;

      if (!alt) return '';

      const parts = alt.trim().split(/\s+/);

      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();

      return parts[0].slice(0, 2).toUpperCase();
    });
    const showImage = computed(() => !!props.src.value && !imgFailed.value);
    const showInitials = computed(() => !showImage.value && !!derivedInitials.value);
    const showFallback = computed(() => !showImage.value && !showInitials.value);
    const avatarLabel = () => {
      const name = (props.alt.value as string | undefined) || null;
      const statusKey = props.status.value as AvatarStatus | undefined;
      const status = statusKey ? STATUS_LABELS[statusKey] : null;

      if (!name && !status) return null;

      if (!name) return `Status: ${status}`;

      if (!status) return name;

      return `${name}, ${status}`;
    };
    const avatarRole = () => (avatarLabel() ? 'img' : null);

    return html`
      <span class="avatar" part="avatar" aria-label="${avatarLabel}" role="${avatarRole}">
        ${() =>
          props.src.value
            ? html`
                <img
                  ref="${attachImgListeners}"
                  part="img"
                  src="${props.src}"
                  alt="${props.alt}"
                  ?hidden="${() => !showImage.value}"
                  aria-hidden="true" />
              `
            : ''}
        ${() =>
          showInitials.value
            ? html`
                <span class="initials" part="initials" aria-hidden="true">${derivedInitials}</span>
              `
            : ''}
        ${() =>
          showFallback.value
            ? html`
                <ore-icon class="icon-fallback" part="fallback" name="user" size="50%"></ore-icon>
              `
            : ''}
      </span>
      ${() =>
        props.status.value
          ? html`
              <span class="status" part="status" data-status="${props.status}" aria-hidden="true"></span>
            `
          : ''}
    `;
  },
  styles: [
    colorThemeMixin,
    roundedVariantMixin,
    sizeVariantMixin({
      lg: { fontSize: 'var(--avatar-font-size, var(--text-base))', size: 'var(--avatar-size, var(--size-14))' },
      md: { fontSize: 'var(--avatar-font-size, var(--text-sm))', size: 'var(--avatar-size, var(--size-10))' },
      sm: { fontSize: 'var(--avatar-font-size, var(--text-xs))', size: 'var(--avatar-size, var(--size-7))' },
    }),
    componentStyles,
  ],
});
